/* ==============================================================================
   Sons de retorno da interface
   ==============================================================================
   Entrar numa sala, alguém entrar, mutar o microfone, começar e parar de
   compartilhar tela. É a diferença entre "cliquei e não sei se funcionou" e
   saber, sem olhar, o que acabou de acontecer.

   Por que os sons são SINTETIZADOS e não arquivos
   ==============================================
   Um pacote de seis .ogg/.mp3 são seis requisições, ~60 KB, e um bug clássico:
   o primeiro som só toca depois de baixar, ou seja, a confirmação chega tarde
   exatamente quando mais importa (a primeira ação da sessão).

   Sintetizar com osciladores custa zero byte de rede, toca no primeiro
   milissegundo, e permite ajustar volume e timbre por código em vez de reeditar
   áudio. Para tons curtos de interface — que é o que estes são — não há perda de
   qualidade perceptível.

   O vocabulário sonoro
   ====================
   Os sons NÃO são arbitrários. Duas notas ASCENDENTES significam entrada; as
   mesmas DESCENDENTES, saída. É a convenção que todo aplicativo de voz usa, e
   copiá-la é o certo: a pessoa já sabe o que significa antes de aprender este
   app.

     entrar        Sol→Dó   sobe    "alguém apareceu"
     sair          Dó→Sol   desce   "alguém foi embora"
     mudo          Lá       curto e grave, um toque só
     desmudo       Mi       curto e claro, um toque só
     tela ligada   Dó→Mi→Sol  arpejo, sobe   "estou transmitindo"
     tela desligada Sol→Mi→Dó arpejo, desce
     erro          Fá#      dissonante, longo

   Por que `AudioContext` PRÓPRIO
   ==============================
   O pipeline de captura tem o dele, com `latencyHint: 'interactive'` e taxa
   fixa em 48 kHz porque o RNNoise exige. Tocar retorno de interface no mesmo
   contexto significa que um som de "mutei" compete com o processamento do
   microfone — no mesmo grafo, no mesmo thread de áudio. Este contexto é
   separado e descartável.
   ============================================================================== */

/** Nome de cada som. Ver o vocabulário no cabeçalho. */
export type FeedbackSound =
  | 'join'
  | 'leave'
  | 'peer-join'
  | 'peer-leave'
  | 'mute'
  | 'unmute'
  | 'screen-start'
  | 'screen-stop'
  | 'error'
  | 'message';

interface Tone {
  /** Frequência em Hz. */
  hz: number;
  /** Atraso do início, em segundos, relativo ao começo do som. */
  at: number;
  /** Duração em segundos. */
  for: number;
  /** Ganho de pico, 0..1, antes do volume global. */
  peak?: number;
  /** Forma de onda. `sine` é o padrão: puro e sem aspereza. */
  wave?: OscillatorType;
}

/*
 * As frequências são notas reais da escala temperada.
 *
 * Usar números redondos ("500 Hz, 800 Hz") produz intervalos dissonantes que
 * soam como alarme de eletrodoméstico. Intervalos musicais — quarta, quinta,
 * terça — soam intencionais, e é isso que separa um som de produto de um bipe.
 */
const G4 = 392.0;
const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const A3 = 220.0;
const E4 = 329.63;
const FS5 = 739.99;

const RECIPES: Record<FeedbackSound, Tone[]> = {
  /* Entrar: quinta ascendente, presente sem ser festivo. */
  join: [
    { hz: G4, at: 0, for: 0.09 },
    { hz: C5, at: 0.07, for: 0.14 },
  ],

  /* Sair: a mesma quinta ao contrário. */
  leave: [
    { hz: C5, at: 0, for: 0.09 },
    { hz: G4, at: 0.07, for: 0.14 },
  ],

  /*
   * Alguém ENTRAR é mais baixo e mais curto que eu entrar.
   *
   * Numa sala movimentada isso toca muitas vezes; ao mesmo volume do meu
   * próprio som viraria ruído e a pessoa desligaria tudo. Metade do ganho é o
   * suficiente para ser percebido e não incomodar.
   */
  'peer-join': [
    { hz: G4, at: 0, for: 0.06, peak: 0.4 },
    { hz: C5, at: 0.05, for: 0.09, peak: 0.4 },
  ],
  'peer-leave': [
    { hz: C5, at: 0, for: 0.06, peak: 0.4 },
    { hz: G4, at: 0.05, for: 0.09, peak: 0.4 },
  ],

  /* Mudo: um toque grave. Desmudo: um toque claro. */
  mute: [{ hz: A3, at: 0, for: 0.1, peak: 0.55 }],
  unmute: [{ hz: E4, at: 0, for: 0.1, peak: 0.55 }],

  /* Tela: arpejo de tríade maior. Três notas dizem "isto é diferente". */
  'screen-start': [
    { hz: C5, at: 0, for: 0.07 },
    { hz: E5, at: 0.06, for: 0.07 },
    { hz: G5, at: 0.12, for: 0.13 },
  ],
  'screen-stop': [
    { hz: G5, at: 0, for: 0.07 },
    { hz: E5, at: 0.06, for: 0.07 },
    { hz: C5, at: 0.12, for: 0.13 },
  ],

  /*
   * Erro: trítono, o intervalo que o ouvido lê como "errado".
   *
   * `triangle` em vez de `sine` para dar um pouco de aspereza. Não é para ser
   * agradável — é para interromper.
   */
  error: [
    { hz: FS5, at: 0, for: 0.18, peak: 0.5, wave: 'triangle' },
    { hz: C5, at: 0.02, for: 0.18, peak: 0.35, wave: 'triangle' },
  ],

  /* Mensagem: um toque só, muito curto e discreto. */
  message: [{ hz: E5, at: 0, for: 0.05, peak: 0.3 }],
};

export interface FeedbackOptions {
  /** Volume global, 0..1. `0` desliga sem desmontar nada. */
  volume?: number;
}

/**
 * Tocador de sons de interface.
 *
 * Uma instância por aplicação. O `AudioContext` é criado na primeira chamada de
 * `play`, não no construtor — ver o comentário lá para o motivo.
 */
export class FeedbackPlayer {
  private context: AudioContext | null = null;
  private volume: number;

  /**
   * Momento do último som de cada tipo, para não repetir em rajada.
   *
   * Oito pessoas entrando numa sala ao mesmo tempo produzem oito eventos no
   * mesmo instante — e oito osciladores somados clipam o alto-falante num
   * estalo. O acúmulo é evitado por um piso de tempo por SOM, não global: mutar
   * o microfone logo depois de entrar tem de soar.
   */
  private lastPlayedAt = new Map<FeedbackSound, number>();

  constructor(options: FeedbackOptions = {}) {
    this.volume = clamp(options.volume ?? 0.35, 0, 1);
  }

  setVolume(volume: number): void {
    this.volume = clamp(volume, 0, 1);
  }

  /**
   * Toca um som. Nunca lança.
   *
   * Retorno de interface que quebra a interface é pior que retorno nenhum: um
   * `throw` daqui subiria por um handler de clique e poderia deixar a UI num
   * estado intermediário. Toda falha é engolida de propósito.
   */
  play(sound: FeedbackSound): void {
    if (this.volume === 0) return;

    try {
      const now = Date.now();
      const last = this.lastPlayedAt.get(sound) ?? 0;
      // 120 ms é abaixo do limiar em que dois sons iguais são percebidos como
      // dois, e acima do intervalo em que eles somam amplitude e estalam.
      if (now - last < 120) return;
      this.lastPlayedAt.set(sound, now);

      const context = this.ensureContext();
      if (!context) return;

      /*
       * O contexto pode estar SUSPENSO, e `resume()` é assíncrono.
       *
       * A política de autoplay nasce com o contexto suspenso até haver gesto do
       * usuário. `resume()` devolve promessa; agendar os osciladores antes dela
       * resolver funciona, porque `currentTime` só avança com o contexto rodando
       * — as notas simplesmente começam quando ele voltar.
       */
      if (context.state === 'suspended') {
        void context.resume().catch(() => undefined);
      }

      const start = context.currentTime;
      for (const tone of RECIPES[sound]) {
        this.schedule(context, tone, start);
      }
    } catch {
      // Sem som. A ação que gerou o som já aconteceu de qualquer forma.
    }
  }

  /**
   * Descarta o contexto.
   *
   * Um `AudioContext` ocupa um thread de áudio do sistema, e navegadores limitam
   * quantos existem por página. Não fechar deixaria um vazando por sessão.
   */
  async dispose(): Promise<void> {
    const context = this.context;
    this.context = null;
    this.lastPlayedAt.clear();
    if (context && context.state !== 'closed') {
      await context.close().catch(() => undefined);
    }
  }

  /* ------------------------------------------------------------------------ */

  /**
   * Cria o contexto na primeira necessidade.
   *
   * No construtor seria criado no boot da aplicação — antes de qualquer gesto,
   * portanto suspenso, e o Chrome registra um aviso no console sobre isso a cada
   * carregamento. Criar sob demanda significa que ele nasce já dentro de um
   * handler de clique, e portanto já rodando.
   */
  private ensureContext(): AudioContext | null {
    if (this.context !== null && this.context.state !== 'closed') {
      return this.context;
    }
    if (typeof AudioContext === 'undefined') return null;

    /*
     * `latencyHint: 'interactive'` também aqui.
     *
     * Som de retorno com 200 ms de atraso não é retorno: a pessoa já
     * interpretou o resultado visualmente antes de ouvir, e o som passa a soar
     * como um segundo evento.
     */
    this.context = new AudioContext({ latencyHint: 'interactive' });
    return this.context;
  }

  private schedule(context: AudioContext, tone: Tone, start: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = tone.wave ?? 'sine';
    oscillator.frequency.value = tone.hz;

    const at = start + tone.at;
    const peak = (tone.peak ?? 0.6) * this.volume;

    /*
     * Envelope obrigatório: sem ele, todo som ESTALA.
     *
     * Um oscilador que começa em amplitude cheia produz uma descontinuidade no
     * sinal, e uma descontinuidade é um clique audível — o mesmo artefato de
     * cortar um arquivo de áudio no meio de um ciclo. A subida de 8 ms é rápida
     * demais para ser percebida como fade e lenta o bastante para eliminar o
     * clique.
     *
     * `exponentialRampToValueAtTime` na descida, e não linear, porque a
     * percepção de volume é logarítmica: uma queda linear soa como se o som
     * sumisse de repente no fim.
     */
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.linearRampToValueAtTime(peak, at + 0.008);
    // O alvo nunca é 0: `exponentialRamp` para zero é indefinido e o navegador
    // ignora a curva inteira, trazendo o estalo de volta.
    gain.gain.exponentialRampToValueAtTime(0.0001, at + tone.for);

    oscillator.connect(gain).connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + tone.for + 0.02);

    /*
     * `onended` desconecta os nós.
     *
     * Um oscilador parado ainda mantém o grafo referenciado. Numa sessão longa
     * com muitos sons isso acumula nós mortos, e o navegador não os coleta
     * enquanto estiverem conectados ao `destination`.
     */
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
    };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
